import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Search as SearchIcon,
    People as PeopleIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { searchUsers, getSuggestedUsers, getAllUsers, PublicUserProfile } from '../services/userService';
import { UserCard } from '../components/social/UserCard';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';

export const DiscoverUsersPage: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestedUsers, setSuggestedUsers] = useState<PublicUserProfile[]>([]);
    const [searchResults, setSearchResults] = useState<PublicUserProfile[]>([]);
    const [allUsers, setAllUsers] = useState<PublicUserProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);

    useEffect(() => {
        loadSuggestedUsers();
        loadAllUsers();
    }, [user?.uid]);

    useEffect(() => {
        if (searchQuery.trim()) {
            handleSearch();
        } else {
            setSearchResults([]);
        }
    }, [searchQuery]);

    const loadSuggestedUsers = async () => {
        if (!user?.uid) return;

        try {
            setLoading(true);
            const suggestions = await getSuggestedUsers(user.uid, 20);
            setSuggestedUsers(suggestions);
        } catch (error) {
            console.error('Erro ao carregar sugestões:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAllUsers = async () => {
        try {
            const users = await getAllUsers(50);
            // Filtrar o próprio usuário
            setAllUsers(users.filter(u => u.uid !== user?.uid));
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        try {
            setSearching(true);
            const results = await searchUsers(searchQuery, 20);
            // Filtrar o próprio usuário
            setSearchResults(results.filter(u => u.uid !== user?.uid));
        } catch (error) {
            console.error('Erro ao buscar usuários:', error);
        } finally {
            setSearching(false);
        }
    };

    const displayUsers = searchQuery.trim()
        ? searchResults
        : currentTab === 0
            ? suggestedUsers
            : allUsers;

    return (
        <>
            <TopBar
                title="Descobrir"
                icon={<PeopleIcon sx={{ fontSize: '0.875rem' }} />}
                showBackButton
                onBackClick={() => navigate('/hostlyclub')}
            />

            <Box
                sx={{
                    backgroundColor: theme.colors.background,
                    minHeight: '100vh',
                    paddingBottom: '120px',
                    paddingTop: '70px',
                }}
            >
                <Container maxWidth="sm" sx={{ padding: 0 }}>
                    {/* Barra de Busca */}
                    <Box sx={{ p: 2, backgroundColor: theme.colors.card }}>
                        <TextField
                            fullWidth
                            placeholder="Buscar usuários..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {searching ? (
                                            <CircularProgress size={20} sx={{ color: theme.colors.text.secondary }} />
                                        ) : (
                                            <SearchIcon sx={{ color: theme.colors.text.secondary }} />
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: theme.radii.medium,
                                    backgroundColor: theme.colors.background,
                                    color: theme.colors.text.primary,
                                },
                            }}
                        />
                    </Box>

                    {/* Tabs - Só mostra se não estiver buscando */}
                    {!searchQuery.trim() && (
                        <Tabs
                            value={currentTab}
                            onChange={(_, newValue) => setCurrentTab(newValue)}
                            variant="fullWidth"
                            sx={{
                                backgroundColor: theme.colors.card,
                                borderBottom: `1px solid ${theme.colors.border}`,
                                '& .MuiTab-root': {
                                    color: theme.colors.text.secondary,
                                    textTransform: 'none',
                                    '&.Mui-selected': {
                                        color: theme.colors.primary,
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: theme.colors.primary,
                                },
                            }}
                        >
                            <Tab label="Sugestões" />
                            <Tab label="Todos" />
                        </Tabs>
                    )}

                    {/* Header da Seção */}
                    <Box sx={{ p: 2, backgroundColor: theme.colors.card, borderBottom: `1px solid ${theme.colors.border}` }}>
                        <Typography variant="subtitle2" sx={{ color: theme.colors.text.secondary }}>
                            {searchQuery.trim()
                                ? `${displayUsers.length} resultado${displayUsers.length !== 1 ? 's' : ''}`
                                : currentTab === 0
                                    ? 'Pessoas que você pode conhecer'
                                    : 'Todos os usuários'
                            }
                        </Typography>
                    </Box>

                    {/* Loading State */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: theme.colors.primary }} />
                        </Box>
                    )}

                    {/* Empty State */}
                    {!loading && displayUsers.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
                            <PeopleIcon sx={{ fontSize: 80, color: theme.colors.text.secondary, mb: 2 }} />
                            <Typography variant="h6" sx={{ color: theme.colors.text.primary, mb: 1 }}>
                                {searchQuery.trim() ? 'Nenhum usuário encontrado' : 'Nenhuma sugestão no momento'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                                {searchQuery.trim()
                                    ? 'Tente buscar por outro nome'
                                    : 'Volte mais tarde para ver novas sugestões'
                                }
                            </Typography>
                        </Box>
                    )}

                    {/* Lista de Usuários */}
                    {!loading && (
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {displayUsers.map((userProfile) => (
                                <UserCard key={userProfile.uid} user={userProfile} showFollowButton />
                            ))}
                        </Box>
                    )}
                </Container>

                <BottomNavigation />
            </Box>
        </>
    );
};
